import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { apiError } from "../api/client";
import { ErrorState } from "./ErrorState";

/**
 * Trạng thái lỗi của một trang / thẻ thống kê (#19850 / STAT-08).
 *
 * Trước đây các trang báo cáo hiện cùng một câu "Không tải được…" cho mọi lỗi, và vài trang (Công
 * nợ, Đánh giá, ô doanh thu ở Tổng quan) còn biến lỗi thành "không có dữ liệu" hoặc "0₫" — tức nói
 * một điều SAI về phòng khám. Ở đây lỗi được gọi đúng tên:
 * - 403 → không có quyền xem (không phải "lỗi máy chủ", và không có nút Thử lại vô ích);
 * - 402 → báo cáo không có trong gói, kèm lối sang trang gói dịch vụ;
 * - 400 `date_range_too_long` → khoảng ngày vượt trần của báo cáo (#19918), nói trần là bao nhiêu
 *   và không có nút Thử lại — thử lại cùng khoảng sẽ ra cùng lỗi;
 * - còn lại → lỗi thật, có nút Thử lại.
 */
export function ReportErrorState({
  error,
  onRetry,
  inline,
  what = "báo cáo này",
}: {
  error: unknown;
  onRetry?: () => void;
  inline?: boolean;
  /** Tên thứ không tải được, ví dụ "báo cáo bệnh nhân". */
  what?: string;
}) {
  const e = apiError(error);
  if (e.status === 403) {
    // Câu của server ("Access denied") là tiếng Anh và không nói người dùng phải làm gì.
    return (
      <ErrorState
        inline={inline}
        error={error}
        title={`Bạn không có quyền xem ${what}`}
        detail="Liên hệ chủ phòng khám để được cấp quyền xem báo cáo."
      />
    );
  }
  if (e.status === 400 && e.code === "date_range_too_long") {
    const max = (e.problem as { maxDays?: number } | null)?.maxDays;
    return (
      <ErrorState
        inline={inline}
        error={error}
        title="Khoảng ngày quá dài"
        detail={
          max
            ? `Báo cáo này xem được tối đa ${max} ngày một lần — chọn khoảng ngắn hơn.`
            : "Chọn khoảng ngày ngắn hơn."
        }
      />
    );
  }
  if (e.status === 402) {
    return (
      <ErrorState
        inline={inline}
        error={error}
        title={`Gói hiện tại chưa có ${what}`}
        detail="Nâng cấp gói dịch vụ để mở báo cáo này."
      >
        <Button asChild variant="outline" size="sm">
          <Link to="/settings/subscription">Xem gói dịch vụ</Link>
        </Button>
      </ErrorState>
    );
  }
  return (
    <ErrorState
      inline={inline}
      error={error}
      title={`Không tải được ${what}`}
      onRetry={onRetry}
    />
  );
}
